import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { apiRequest } from '@/src/services';
import { getValidSelectValue, getZeroOption, type Option, Constants } from '@/src/utils';
import {
    type InitialOptions,
    type ClassOption,
    type SchoolClassOptions,
    type ExamOption,
    type FilterState,
} from '../models/filterDTOs';
import {
    ErrorMessage,
    FilterIcon,
    ICON_COLORS,
    SimpleButton,
    SimpleSingleSelect,
    FilterBarContainer,
} from '@/src/ui';

const LoadingOption = getZeroOption('Loading...');
const SelectDistrictOption = getZeroOption('Select District');
const SelectSchoolOption = getZeroOption('Select School');
const SelectGradeOption = getZeroOption('Select Grade');
const AllOption = getZeroOption('All');
const SelectExamOption = getZeroOption('All Assessments');
const NoClassOption = getZeroOption('No Classes Available');
const NoExamOption = getZeroOption('No Assessments Available');
const AllStudentOption = getZeroOption('All');

const REQUIRED_FIELDS = ['districtId', 'schoolId', 'gradeLevelMasterId'] as const;

function getMissingFields(fs: FilterState): string[] {
    return REQUIRED_FIELDS.filter(
        (key) => fs[key] === 0 || Number(fs[key]) === Number(SelectDistrictOption.id)
    ).map((key) => Constants.fieldLabels[key] || key);
}

const sortByName = <T extends { name: string }>(items: T[]): T[] =>
    [...items].sort((a, b) => a.name.localeCompare(b.name));

const loadingProps = (opts: Option[], val: number, loading: boolean) =>
    loading
        ? { options: [LoadingOption], value: 0, disabled: true }
        : { options: opts, value: val };

interface StudentHistoryFiltersProps {
    refreshTableData: (filters: FilterState) => void;
    filterState: FilterState;
    setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
    mode: 'normal' | 'student-limited';
    onExamNameChange?: (name: string) => void;
}

const StudentHistoryFilters: React.FC<StudentHistoryFiltersProps> = ({
    refreshTableData,
    filterState,
    setFilterState,
    mode,
    onExamNameChange,
}) => {
    const isLimited = mode === 'student-limited';

    const [errorMsg, setErrorMsg] = useState('');
    const [fieldsChanged, setFieldsChanged] = useState(false);
    const [districtOptions, setDistrictOptions] = useState<Option[]>([LoadingOption]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([SelectSchoolOption]);
    const [gradeLevelMasterOptions, setGradeLevelMasterOptions] = useState<Option[]>([
        LoadingOption,
    ]);
    const [classOptions, setClassOptions] = useState<Option[]>([AllOption]);
    const [examOptions, setExamOptions] = useState<Option[]>([SelectExamOption]);
    const [studentOptions, setStudentOptions] = useState<Option[]>([AllStudentOption]);

    const [classData, setClassData] = useState<ClassOption[]>([]);
    const [examDataAll, setExamDataAll] = useState<ExamOption[] | null>(null);

    const [filtersLoading, setFiltersLoading] = useState(true);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [examLoading, setExamLoading] = useState(false);
    const [studentLoading, setStudentLoading] = useState(false);

    /** 1. Fetch initial district + grade options. */
    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getDistrict=true'
                );
                const { gradeLevelMasterOptions: grades, districtOptions: districts } = res.data;
                const sortedDistricts = sortByName(districts ?? []);

                if (isLimited) {
                    setGradeLevelMasterOptions(grades ?? []);
                    setDistrictOptions(sortedDistricts);

                    const firstDistrictId = Number(sortedDistricts[0]?.id);
                    const firstGradeId = Number((grades ?? [])[0]?.id);

                    setFilterState((prev) => ({
                        ...prev,
                        districtId: firstDistrictId,
                        gradeLevelMasterId: firstGradeId,
                        classId: Number(AllOption.id),
                        examId: Number(SelectExamOption.id),
                        studentId: Number(AllStudentOption.id),
                    }));
                } else {
                    setGradeLevelMasterOptions([SelectGradeOption, ...(grades ?? [])]);
                    setDistrictOptions([SelectDistrictOption, ...sortedDistricts]);
                }
            } catch {
                setErrorMsg(
                    'Failed to load initial filter options. Please refresh the page or try again.'
                );
            } finally {
                setInitialLoadComplete(true);
            }
        })();
    }, [isLimited, setFilterState]);

    useEffect(() => {
        if (!initialLoadComplete) return;

        if (filterState.districtId && filterState.districtId !== Number(SelectDistrictOption.id)) {
            fetchSchoolClassOptions(filterState.districtId).then(() => setFiltersLoading(false));
        } else {
            setFiltersLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLoadComplete, filterState.districtId]);

    const fetchSchoolClassOptions = async (districtId: number) => {
        if (!districtId || districtId === Number(SelectDistrictOption.id)) {
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([AllOption]);
            setFilterState((prev) => ({
                ...prev,
                schoolId: Number(SelectSchoolOption.id),
                classId: Number(AllOption.id),
            }));
            return;
        }

        try {
            const res = await apiRequest<SchoolClassOptions>(
                'GET',
                `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
            );
            const data = res.data;
            const sortedSchools = sortByName(data.schoolOptions ?? []);
            setSchoolOptions([SelectSchoolOption, ...sortedSchools]);

            setFilterState((prev) => {
                const validSchoolId = sortedSchools.some(
                    (s) => Number(s.id) === Number(prev.schoolId)
                )
                    ? prev.schoolId
                    : Number(SelectSchoolOption.id);
                return { ...prev, schoolId: validSchoolId };
            });

            const sortedClasses = sortByName(data.classOptions ?? []);
            setClassData(sortedClasses);

            setClassOptions([AllOption]);
            setErrorMsg('');
        } catch {
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([AllOption]);
            setErrorMsg(
                'Failed to fetch school and class options. Please refresh the page or try again.'
            );
        }
    };

    // --- UPDATE CLASSES ---
    useEffect(() => {
        if (
            filterState.schoolId &&
            filterState.schoolId !== Number(SelectSchoolOption.id) &&
            filterState.gradeLevelMasterId &&
            filterState.gradeLevelMasterId !== Number(SelectGradeOption.id) &&
            classData.length > 0
        ) {
            updateClassOptions(classData, filterState.gradeLevelMasterId, filterState.schoolId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classData, filterState.schoolId, filterState.gradeLevelMasterId]);

    const updateClassOptions = (data: ClassOption[], gradeId: number, schoolId: number) => {
        if (
            !gradeId ||
            gradeId === Number(SelectGradeOption.id) ||
            !schoolId ||
            schoolId === Number(SelectSchoolOption.id)
        ) {
            setClassOptions([AllOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(AllOption.id) }));
            return;
        }

        const filtered = data
            .filter((c) => c.schoolId === schoolId)
            .filter(
                (c) => !c.gradeLevelMasterIds?.length || c.gradeLevelMasterIds.includes(gradeId)
            )
            .map((c) => ({ id: c.id, name: c.name }));

        if (!filtered.length) {
            setClassOptions([NoClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(NoClassOption.id) }));
        } else {
            setClassOptions([AllOption, ...filtered]);
            setFilterState((prev) => {
                const classExists = filtered.some((c) => Number(c.id) === Number(prev.classId));
                return {
                    ...prev,
                    classId: classExists ? prev.classId : Number(AllOption.id),
                };
            });
        }
    };

    useEffect(() => {
        maybeFetchExamOptions(filterState.gradeLevelMasterId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState.gradeLevelMasterId, filterState.classId]);

    const maybeFetchExamOptions = async (gradeId: number) => {
        if (!gradeId || gradeId === Number(SelectGradeOption.id)) {
            setExamDataAll(null);
            setExamLoading(false);
            return;
        }

        setExamLoading(true);
        try {
            const res = await apiRequest<ExamOption[]>(
                'GET',
                `utility/filterData/assignedExams?DistrictId=${filterState.districtId}&SchoolId=${filterState.schoolId}&GradeLevelMasterId=${gradeId}&ClassId=${filterState.classId ?? 0}`
            );
            const data = (res.data ?? []).filter((e) => e.schoolIds?.length);
            setExamDataAll(sortByName(data));
        } catch {
            setExamDataAll([]);
            setErrorMsg('Failed to load assessment options. Please refresh the page or try again.');
        } finally {
            setExamLoading(false);
        }
    };

    useEffect(() => {
        filterExamsLocally(filterState.schoolId, filterState.classId, examDataAll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [examDataAll, filterState.schoolId, filterState.classId]);

    const filterExamsLocally = (
        schoolId: number,
        classId: number,
        allExams: ExamOption[] | null
    ) => {
        if (!allExams) return;

        if (allExams.length === 0) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(NoExamOption.id) }));
            return;
        }

        let filtered = allExams;

        if (schoolId && schoolId !== Number(SelectSchoolOption.id)) {
            filtered = filtered.filter(
                (e) => !e.schoolIds?.length || e.schoolIds.includes(schoolId)
            );
        }

        if (classId && classId !== Number(AllOption.id) && classId !== Number(NoClassOption.id)) {
            filtered = filtered.filter((e) => !e.classIds?.length || e.classIds.includes(classId));
        }

        if (!filtered.length) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(NoExamOption.id) }));
        } else {
            setExamOptions([SelectExamOption, ...filtered]);
            setFilterState((prev) => {
                const exists = filtered.some((e) => Number(e.id) === Number(prev.examId));
                return { ...prev, examId: exists ? prev.examId : Number(SelectExamOption.id) };
            });
        }
    };

    useEffect(() => {
        if (initialLoadComplete) {
            fetchStudentOptions(filterState.classId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLoadComplete, filterState.classId]);

    const fetchStudentOptions = async (classId: number) => {
        if (!classId || classId === Number(AllOption.id) || classId === Number(NoClassOption.id)) {
            setStudentOptions([AllStudentOption]);
            setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentOption.id) }));
            return;
        }

        setStudentLoading(true);
        try {
            const res = await apiRequest<{ studentOptions: Option[] }>(
                'GET',
                `utility/filterData?getStudentsByClass=true&classId=${classId}`
            );
            const data = res.data.studentOptions ?? [];
            setStudentOptions([AllStudentOption, ...sortByName(data)]);
            setFilterState((prev) => {
                const exists = data.some((s) => Number(s.id) === Number(prev.studentId));
                return {
                    ...prev,
                    studentId: exists ? prev.studentId : Number(AllStudentOption.id),
                };
            });
        } catch {
            setStudentOptions([AllStudentOption]);
            setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentOption.id) }));
        } finally {
            setStudentLoading(false);
        }
    };

    const selectSx = { minWidth: 140, maxWidth: 280 };
    const selectSxWide = { minWidth: 220, maxWidth: 280 };

    if (isLimited) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="district-select"
                    label="District *"
                    {...loadingProps(
                        districtOptions,
                        Number(
                            getValidSelectValue(
                                filterState.districtId,
                                districtOptions,
                                districtOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            districtId: newId,
                            schoolId: Number(SelectSchoolOption.id),
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(AllOption.id),
                            examId: Number(SelectExamOption.id),
                            studentId: Number(AllStudentOption.id),
                        });
                        fetchSchoolClassOptions(newId);
                    }}
                    sx={selectSx}
                />
                <SimpleSingleSelect
                    name="school-select"
                    label="School *"
                    {...loadingProps(
                        schoolOptions,
                        Number(
                            getValidSelectValue(
                                filterState.schoolId,
                                schoolOptions,
                                schoolOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            schoolId: newId,
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(AllOption.id),
                            examId: Number(SelectExamOption.id),
                            studentId: Number(AllStudentOption.id),
                        });
                    }}
                    sx={selectSxWide}
                    disabled={
                        filtersLoading || filterState.districtId === Number(SelectDistrictOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="grade-select"
                    label="Grade Level *"
                    {...loadingProps(
                        gradeLevelMasterOptions,
                        Number(
                            getValidSelectValue(
                                filterState.gradeLevelMasterId,
                                gradeLevelMasterOptions,
                                gradeLevelMasterOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            gradeLevelMasterId: newId,
                            classId: Number(AllOption.id),
                            examId: Number(SelectExamOption.id),
                            studentId: Number(AllStudentOption.id),
                        });
                    }}
                    sx={selectSx}
                    disabled={
                        filtersLoading || filterState.schoolId === Number(SelectSchoolOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="class-select"
                    label="Class"
                    {...loadingProps(
                        classOptions,
                        Number(
                            getValidSelectValue(
                                filterState.classId,
                                classOptions,
                                classOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            classId: newId,
                            examId: Number(SelectExamOption.id),
                            studentId: Number(AllStudentOption.id),
                        });
                    }}
                    sx={selectSxWide}
                    disabled={
                        filtersLoading ||
                        !filterState.districtId ||
                        filterState.districtId === Number(SelectDistrictOption.id) ||
                        !filterState.schoolId ||
                        filterState.schoolId === Number(SelectSchoolOption.id) ||
                        !filterState.gradeLevelMasterId ||
                        filterState.gradeLevelMasterId === Number(SelectGradeOption.id)
                    }
                />
            </FilterBarContainer>

            <FilterBarContainer>
                <SimpleSingleSelect
                    name="exam-select"
                    label="Assessment"
                    {...loadingProps(
                        examOptions,
                        Number(
                            getValidSelectValue(
                                filterState.examId,
                                examOptions,
                                examOptions[0]?.id ?? 0
                            )
                        ),
                        examLoading || filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const value = Number(e.target.value);
                        setFilterState({ ...filterState, examId: value });
                        onExamNameChange?.(
                            examOptions.find((o) => Number(o.id) === value)?.name ?? ''
                        );
                    }}
                    sx={selectSxWide}
                    disabled={
                        examLoading ||
                        filtersLoading ||
                        filterState.gradeLevelMasterId === Number(SelectGradeOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="student-select"
                    label="Student"
                    {...loadingProps(
                        studentOptions,
                        Number(
                            getValidSelectValue(
                                filterState.studentId,
                                studentOptions,
                                studentOptions[0]?.id ?? 0
                            )
                        ),
                        studentLoading || filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState({ ...filterState, studentId: Number(e.target.value) });
                    }}
                    sx={selectSxWide}
                    disabled={
                        studentLoading ||
                        filtersLoading ||
                        filterState.classId === Number(AllOption.id)
                    }
                />
                <SimpleButton
                    onClick={() => {
                        const missing = getMissingFields(filterState);
                        if (missing.length) {
                            setErrorMsg(`Please select: ${missing.join(', ')} before filtering.`);
                            return;
                        }
                        setErrorMsg('');
                        setFieldsChanged(false);
                        refreshTableData(filterState);
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ minHeight: 40, height: 40, alignSelf: 'center', padding: '8px 25px' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: 16, height: 16, marginRight: 8 }}
                    />
                    Filter
                </SimpleButton>
            </FilterBarContainer>

            <ErrorMessage error={errorMsg} />
        </Box>
    );
};

export default StudentHistoryFilters;
